import { ImmutableObject } from "@hookstate/core"
import { Settings } from "@mui/icons-material"
import HighlightOffIcon from "@mui/icons-material/HighlightOff"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import { CardActions, CardContent, Collapse, IconButton } from "@mui/material"
import Checkbox from "@mui/material/Checkbox"

import { ChangeEvent, FC, useState } from "react"

import {
  AdvancedFormFieldDependency,
  AdvancedForms,
} from "src/components/common/advancedForm"
import { Button } from "src/components/common/button"
import { Card } from "src/components/common/card"
import { LazyImage } from "src/components/common/lazyImage"
import { DefaultImage } from "src/components/common/lazyImage/DefaultImage"
import { create } from "src/helpers/bem"

import { deepClone } from "src/helpers/deepClone"
import { formatPrice } from "src/helpers/price"
import { useTranslation } from "src/hooks/translation"
import { useLocale } from "src/states/locale"
import {
  CartProduct,
  getCartProductAmount,
  mergeCart,
  useCart,
} from "src/states/shop/carts"

import { mergeProductAttributes } from "src/states/shop/productAttributes"
import { ProductVariant } from "src/states/shop/productVariants"
import { xRMApiField, xRMApiFieldValueDictionary } from "src/types/xRM"

import styles from "./ShopCartProductCard.module.scss"

const bem = create(styles, "ShopCartProductCard")

export type ShopCartProductCardProps = {
  item: ImmutableObject<CartProduct>
  selected: boolean
  onSelect: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void
}

export const ShopCartProductCard: FC<ShopCartProductCardProps> = ({
  item,
  selected,
  onSelect,
}) => {
  const locale = useLocale()
  const translation = useTranslation()
  const m = translation.messages.pages.shop.cart.productCard
  const productVariant = item.productVariant!
  const productImage = productVariant?.images?.[0]
  const cart = useCart()
  const originProduct = cart.products.find((product) => product.id == item.id)
  const [loading, setLoading] = useState(false)

  const {
    basePrice,
    mandatoryProducts,
    quantity,
    productCalculation,
    productAttributeDependencies,
    unit,
    name,
  } = productVariant

  const unitPrice = (basePrice ?? 0) * (quantity ?? 1)

  const total =
    unitPrice! * getCartProductAmount(item) +
    mandatoryProducts!.reduce(
      (sum, mandatory) =>
        sum + (mandatory.basePrice ?? 0) * (mandatory.quantity ?? 1),
      0,
    )

  const hasAttributeValue = (attribute: xRMApiFieldValueDictionary) => {
    return attribute.value !== undefined && attribute.value !== null
  }

  const hasAttributes = item.productAttributeValues?.some(
    (attribute) =>
      (attribute.referenceType === "ProductAttribute" ||
        attribute.referenceType === "PONumber" ||
        attribute.referenceType === "Pretix") &&
      attribute.value != null,
  )

  const hasMandatoryProducts =
    item.productVariant?.mandatoryProducts &&
    item.productVariant?.mandatoryProducts.length > 0

  const renderAttributeLabelAndValue = ({
    label,
    value,
  }: {
    label: any
    value: any
  }) => {
    return (
      <div className={bem("attributes")}>
        <span className={bem("attributes__label")}>{label}:</span>
        <div>{value}</div>
      </div>
    )
  }

  const getFieldValue = (field: xRMApiField, attributeValue: any) => {
    switch (field?.type) {
      case "select":
        if (Array.isArray(attributeValue)) {
          return attributeValue
            .map((v) => field.items?.find((i) => i.key === v)?.value)
            .join(", ")
        } else {
          return field.items?.find((i) => i.key === attributeValue)?.value
        }
      case "checkbox":
        return attributeValue?.toString() === "true"
          ? m.checkboxValueTrue
          : m.checkboxValueFalse
      case "file":
        return m.fileValueText
      default:
        return attributeValue?.toString()
    }
  }

  const parseAttribute = (attribute: xRMApiFieldValueDictionary) => {
    const fields = item.productVariant?.productAttributesForm?.fields
    let field = fields?.find((f) => f.field === attribute.field)

    if (attribute.referenceType === "ProductAttribute") {
      field = fields?.find((f) => f.id === attribute.field)
    }

    let label = field?.label
    if (attribute.referenceType === "Pretix") {
      label = `Pretix ${field?.label}`
    }

    const value = getFieldValue(field as xRMApiField, attribute.value)
    return renderAttributeLabelAndValue({ label, value })
  }

  const openProductDetails = () => {
    const productAttributesFormFields =
      productVariant.productAttributesForm?.fields?.map((field) => {
        const attrValue = item.productAttributeValues?.find(
          (attr) => attr.field == field.field,
        )?.value
        return { ...field, value: attrValue }
      })

    const productAttributesForm = {
      ...productVariant.productAttributesForm,
      fields: productAttributesFormFields,
    }

    mergeProductAttributes({
      open: true,
      cartProductId: item.id,
      productVariant: deepClone<ProductVariant>(productVariant),
      form: [deepClone<AdvancedForms>(productAttributesForm)],
      dependencies: deepClone<AdvancedFormFieldDependency[]>(
        productAttributeDependencies,
      ),
    })
  }

  return (
    <Card>
      <CardContent className={bem("contents")}>
        <div className={bem()}>
          <div className={bem("select")}>
            <Checkbox checked={selected} onChange={onSelect} />
          </div>
          <div
            className={bem(
              productCalculation === "Fix" ? "amount" : "amount__invisible",
            )}
          >
            <div className={bem("counter")}>
              <div className={bem("counter__widget")}>
                <IconButton
                  color="primary"
                  disabled={
                    getCartProductAmount(item) >=
                      (item?.productVariant?.maximumOrderAmount ?? 1000000) ||
                    loading
                  }
                  onClick={async () => {
                    try {
                      setLoading(true)
                      await mergeCart(item, (amount) => amount + 1)
                    } finally {
                      setLoading(false)
                    }
                  }}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
                <div className={bem("counter__sum")}>
                  {getCartProductAmount(item)}
                </div>
                <IconButton
                  color="primary"
                  disabled={getCartProductAmount(item) <= 1 || loading}
                  onClick={async () => {
                    try {
                      setLoading(true)
                      await mergeCart(item, (amount) => amount - 1)
                    } finally {
                      setLoading(false)
                    }
                  }}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </div>
              <div className={bem("counter__unit")}>{unit}</div>
            </div>
          </div>
          <div className={bem("image")}>
            {productImage?.src ? (
              <LazyImage
                alt={productImage.alt ?? name ?? ""}
                src={productImage.src}
              />
            ) : (
              <DefaultImage />
            )}
          </div>
          <div className={bem("info")}>
            <div className={bem("info__name")}>{name}</div>
            {productCalculation !== "Charges" && (
              <div className={bem("sum")}>
                à{formatPrice(unitPrice, locale)}
              </div>
            )}
            <div className={bem("total")}>{formatPrice(total, locale)}</div>
          </div>
        </div>
      </CardContent>
      {hasMandatoryProducts && (
        <CardContent className={bem("attributescontent")}>
          <div className={bem("attributes")}>
            <div className={bem("attributes__label")}>
              {m.mandatoryProductsLabel}
            </div>
            <div className={bem("attributes__value")}>
              {item?.productVariant?.mandatoryProducts?.map((p, index) => (
                <div key={index}>
                  {p.quantity != null
                    ? `${p.quantity * getCartProductAmount(item)} x ${
                        p.name
                      } (à ${formatPrice(p.basePrice)})`
                    : `${p.name} (à ${formatPrice(p.basePrice)})`}
                  {index <
                    (item?.productVariant?.mandatoryProducts?.length ?? 0) -
                      1 && ", "}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}
      <CardActions className={bem("actions")}>
        <div>
          <Button
            startIcon={<HighlightOffIcon />}
            title={m.remove}
            variant="text"
            onClick={async () => await mergeCart(originProduct!, (_) => 0)}
          />
          <Button
            startIcon={<Settings />}
            title={m.expand}
            variant="text"
            onClick={() => openProductDetails()}
          />
        </div>
      </CardActions>
      <Collapse unmountOnExit in={true} timeout="auto">
        {/* <div>
            {item.productAttributeValues
              ?.filter((a) => a.referenceType !== "OrderAmount")
              .map((attribute) =>
                hasAttributeValue(attribute) ? (
                  <div key={attribute.field}>
                    <div>{parseAttribute(attribute)}</div>
                  </div>
                ) : null,
              )}
          </div> */}
      </Collapse>
    </Card>
  )
}
